import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAssessmentResultsDetailed } from "../../../../services/user/course";
import { Icon } from "@iconify/react";
import StatusBadge from '../../../../components/ui/StatusBadge';

function Result() {
  const location = useLocation();
  const navigate = useNavigate();
  const assessmentId = location.state?.assessmentId || new URLSearchParams(location.search).get('assessmentId');
  const [assessmentResults, setAssessmentResults] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (assessmentId) fetchResults();
    else setLoadError('Open an assessment result from your course to review it.');
  }, [assessmentId]);

  async function fetchResults() {
    try {
      setLoadError('');
      const response = await getAssessmentResultsDetailed(assessmentId);
      if (!Array.isArray(response?.data?.questions)) throw new Error('Results are unavailable');
      setAssessmentResults(response.data);
    } catch (error) {
      setLoadError('The assessment results could not be loaded. Please try again.');
    }
  }

  const handlePrevQuestion = () => {
    setCurrentQuestionIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextQuestion = () => {
    if (assessmentResults) {
      setCurrentQuestionIndex((prev) =>
        Math.min(assessmentResults.questions.length - 1, prev + 1)
      );
    }
  };

  const handlePlayVideo = (videoId, timestamp, videoUrl, upload_view_type) => {
    navigate("/user/resultvideo", {
      state: {
        videoId,
        timestamp,
        videoUrl,
        upload_view_type,
        assessmentId,
      },
    });
  };

  const renderMediaContent = (type, url, content, showContentValue = false) => (
    <div className="tw-my-4 tw-text-center">
      {showContentValue && <div className="![font-weight:var(--font-weight-semibold)] tw-mb-2 tw-font-semibold">{content}</div>}
      {type === "image" && (
        <img
          src={url}
          alt={content}
          className="tw-max-w-full tw-rounded-xl"
          style={{
            maxWidth: "300px",
            maxHeight: "200px",
            objectFit: "contain",
          }}
        />
      )}
      {type === "audio" && (
        <audio
          controls
          src={url}
          className="tw-w-full"
          style={{ maxWidth: "300px" }}
        >
          Your browser does not support audio.
        </audio>
      )}
    </div>
  );

  if (loadError) {
    return (
      <div className="tw-mx-auto tw-max-w-xl tw-p-6 tw-text-center">
        <p>{loadError}</p>
        <button type="button" className="ui-button ui-button-secondary" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  if (!assessmentResults) {
    return <div className="!tw-text-[var(--secondary-color)] tw-text-center tw-py-12 tw-text-lg tw-text-slate-500">Loading...</div>;
  }

  const currentQuestion = assessmentResults.questions[currentQuestionIndex];
  if (!currentQuestion) {
    return (
      <div className="!tw-text-[var(--secondary-color)] tw-text-center tw-py-12 tw-text-lg tw-text-slate-500">
        Question data not available
      </div>
    );
  }

  let userAnswers = [];
  try {
    const parsed = currentQuestion.user_answer ? JSON.parse(currentQuestion.user_answer) : [];
    userAnswers = Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    userAnswers = [];
  }
  const correctOptions = currentQuestion.correct_option || [];
  const isUnanswered = currentQuestion.user_answer === null;

  return (
    <div className="tw-ml-auto tw-mr-auto tw-w-full tw-max-w-[80rem] ui-container tw-p-0 md:tw-py-6">
      <div className="tw-bg-white tw-shadow-sm tw-p-6 tw-rounded-xl">
        {/* Question Header */}
        <div className="tw-mb-6">
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-center">
            <div className="md:tw-text-left tw-text-center">
              <h4 className="tw-font-bold tw-mb-1">
                Question {currentQuestionIndex + 1} of{" "}
                {assessmentResults.questions.length}
              </h4>
              {currentQuestion.video_timestamp && (
                <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm">
                  Timestamp: {currentQuestion.video_timestamp}
                </div>
              )}
            </div>

            {currentQuestion.ref_video_id &&
              currentQuestion.video_timestamp && (
                <div className="tw-mt-4 md:tw-mt-0">
                  <button
                    type="button"
                    className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-primary ui-button-sm"}
                    style={{ border: "none" }}
                    onClick={() =>
                      handlePlayVideo(
                        currentQuestion.ref_video_id,
                        currentQuestion.video_timestamp,
                        currentQuestion.video_url,
                        currentQuestion.upload_view_type
                      )
                    }
                  >
                    <Icon icon="mdi:play-circle" /> Play Video
                  </button>
                </div>
              )}
          </div>
        </div>

        {/* Question Text */}
        <div className="tw-mb-6 tw-text-left">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-2">
            <p className="![font-weight:var(--font-weight-semibold)] tw-font-semibold tw-mb-0">{currentQuestion.question}</p>
            {/* Answer Type Badge */}
            <StatusBadge tone={currentQuestion.answer_type === 'multiple' ? 'info' : 'neutral'}>
              <Icon
                icon={currentQuestion.answer_type === 'multiple' ? 'mdi:checkbox-multiple-marked' : 'mdi:radiobox-marked'}
                className="tw-me-1"
                width="14"
              />
              {currentQuestion.answer_type === 'multiple' ? 'Multiple Choice' : 'Single Choice'}
            </StatusBadge>
          </div>
          {(currentQuestion.question_content_type === "image" ||
            currentQuestion.question_content_type === "audio") &&
            renderMediaContent(
              currentQuestion.question_content_type,
              currentQuestion.question_media_url,
              currentQuestion.question
            )}
        </div>

        {/* Options List */}
        <div className="tw-mb-4">
          <h6 className="!tw-text-[var(--secondary-color)] tw-text-slate-500">
            Answers
            <small className="!tw-text-[var(--secondary-color)] tw-ms-2 tw-text-slate-500">
              ({currentQuestion.answer_type === 'single'
                ? 'Select the right answer'
                : 'Select all correct answers - missing any one makes the answer wrong'})
            </small>
          </h6>
          <div className="tw-flex tw-flex-col tw-gap-4">
            {currentQuestion.options?.map((option) => {
              // Handle both string and number comparisons for user answers
              const isUserSelected = userAnswers.some((id) => String(id) === String(option.id));
              const isCorrectAnswer = correctOptions.includes(
                option.content_value
              );

              return (
                <div
                  key={option.id}
                  className={`[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-border tw-p-4 tw-rounded-xl   ${
                    isUserSelected || isCorrectAnswer ? "tw-bg-slate-50" : ""
                  }`}
                >
                  {option.content_type === "text" ? (
                    <p className="tw-mb-2">{option.content_value}</p>
                  ) : (
                    renderMediaContent(
                      option.content_type,
                      option.media_url,
                      option.content_value,
                      true
                    )
                  )}
                  <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-2">
                    {isUserSelected && (
                      <StatusBadge tone="info">Your Choice</StatusBadge>
                    )}
                    {isCorrectAnswer && (
                      <StatusBadge tone="success">Correct Answer</StatusBadge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Feedback */}
        <div className="tw-text-center tw-mt-6">
          {isUnanswered ? (
            <StatusBadge tone="warning">
              <Icon icon="mdi:alert-circle-outline" />
              Unanswered
            </StatusBadge>
          ) : currentQuestion.user_is_correct ? (
            <StatusBadge tone="success">
              <Icon icon="mdi:check-circle" />
              Correct
            </StatusBadge>
          ) : (
            <StatusBadge tone="danger">
              <Icon icon="mdi:close-circle" />
              Incorrect
            </StatusBadge>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mt-6 tw-pt-4 tw-border-t"}>
          <div className="tw-min-w-[0px] ui-column tw-w-full tw-flex tw-flex-col md:tw-flex-row tw-justify-center tw-gap-4 tw-mt-2">
            <button
              onClick={handlePrevQuestion}
              disabled={currentQuestionIndex === 0}
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-secondary tw-w-full md:tw-w-auto tw-px-6"}
            >
              <Icon icon="mdi:chevron-left" /> Previous
            </button>
            <button
              onClick={handleNextQuestion}
              disabled={
                currentQuestionIndex === assessmentResults.questions.length - 1
              }
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-w-full md:tw-w-auto tw-px-6"}
            >
              Next <Icon icon="mdi:chevron-right" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Result;
